import React from 'react'
import { useSelector } from 'react-redux'

import { RootStateTypeExtra } from '@store/types'

import {
  selectMatrixDataByPrecinctIdsFromResult,
  useGetMatrixDataByPrecinctIdsQuery,
} from '@api/houseAndLand'

import { ExtendedStage } from '@adUtilities/components/compare-property/utils/use-sync-comparable-properties-from-session'
import { ComparableLot } from '@adUtilities/types'

const useLotComparisonStagesData = () => {
  const { projectName, isActive, properties, activePrecinctId } = useSelector(
    (state: RootStateTypeExtra) => {
      const {
        firestore: { session },
      } = state
      return {
        projectName: state.projectIdentity.projectName,
        isActive: session?.propertyCompare?.active,
        properties: session?.propertyCompare?.properties,
        activePrecinctId: session?.houseAndLand?.activePrecinctId,
      }
    }
  )
  const [visitedPrecinctIds, setVisitedPrecinctIds] = React.useState<string[]>(
    []
  )
  const precinctIds = React.useMemo(() => {
    const precinctIdsInComparison = (properties as ComparableLot[])?.map(
      (item) => item.references?.precinctId
    )
    const allIds = [...(precinctIdsInComparison || []), ...visitedPrecinctIds]
    if (activePrecinctId) {
      allIds.push(activePrecinctId)
    }
    return Array.from(new Set(allIds))
  }, [properties, visitedPrecinctIds, activePrecinctId])

  const { data, isLoaded } = useGetMatrixDataByPrecinctIdsQuery(
    {
      projectName,
      precinctIdsOrLabels: precinctIds,
      lots: true,
    },
    {
      selectFromResult: selectMatrixDataByPrecinctIdsFromResult,
      skip: !isActive,
    }
  )

  const stages = React.useMemo(() => {
    const result: ExtendedStage[] = []

    data.forEach((matrixData, index) => {
      const stageMap = matrixData.stages || {}
      const stageArray = Object.keys(stageMap).map<ExtendedStage>((key) => ({
        ...stageMap[key],
        precinctId: precinctIds[index],
      }))
      result.push(...stageArray)
    })
    return result
  }, [data, precinctIds])

  const refreshPrecinctStageData = React.useCallback((precinctId: string) => {
    setVisitedPrecinctIds((prevVal) => {
      if (prevVal.includes(precinctId)) {
        return prevVal
      }
      return [...prevVal, precinctId]
    })
  }, [])

  return {
    isLoaded,
    stages,
    refreshPrecinctStageData,
  }
}

export default useLotComparisonStagesData
